/**
 * Wire types + runtime type-guards for the BFF SSE stream payloads.
 *
 * Stream A PR 2 / commit M3 — the trust-boundary types for the
 * ``status`` / ``detection`` / ``alert`` / ``alert-dismissed`` SSE
 * events fanned out by
 * :file:`packages/meridian_bff/src/meridian_bff/_query_sse.py` on
 * ``GET /api/query/sse``. (The data-plane ``event`` kind was removed in
 * the ADR-032 cascade — PR6; Detection + Alert supersede it.)
 *
 * # Hand-rolled rather than codegen'd
 *
 * Unlike :file:`snapshot-types.ts` (which re-exports from
 * :file:`@/lib/api-types.gen` because the BFF declares a
 * ``response_model=SnapshotResponse`` on its FastAPI route), the SSE
 * route returns a :class:`StreamingResponse` which FastAPI cannot
 * decorate with a typed response model for OpenAPI documentation.
 * The Python-side :class:`SseStatusPayload` /
 * :class:`SseDetectionPayload` / :class:`SseAlertPayload` Pydantic
 * models (in
 * :file:`packages/meridian_bff/src/meridian_bff/_query_api.py`) carry
 * the wire contract on the BFF side but never surface in
 * ``packages/meridian_bff/api/openapi.yaml`` because no GET route
 * references them. The TS shapes here are therefore hand-rolled to
 * match the Python Pydantic models verbatim.
 *
 * # Drift risk + the codegen-handoff trigger
 *
 * The hand-roll is acceptable because:
 *   1. The Python models are tested against fixtures in
 *      ``packages/meridian_bff/tests/test_query_sse.py``.
 *   2. The :func:`isSseStatusPayload` / :func:`isSseDetectionPayload`
 *      / :func:`isSseAlertPayload` runtime guards reject any payload
 *      that doesn't match the expected shape — silent drift surfaces
 *      as a 5xx-shaped failure per CLAUDE.md §8.1.
 *
 * **Codegen-handoff trigger** — if a future Lane A
 * :file:`docs/ui-api-change-requests/*` surfaces these payloads
 * through OpenAPI (e.g., by adding a doc-only
 * ``/api/query/_sse-schemas`` endpoint with
 * ``response_model=Union[SseStatusPayload, SseDetectionPayload, ...]``),
 * this file becomes a re-export shim mirroring
 * :file:`snapshot-types.ts`. The runtime guards below stay (they
 * cover trust-boundary validation regardless of source).
 *
 * # Why we still need runtime guards
 *
 * TypeScript types are erased at runtime. A server bug, schema
 * drift, MSW fixture typo, or compromised upstream could deliver a
 * payload that doesn't match the typed contract. CLAUDE.md §2.2
 * mandates ``zod`` at trust boundaries; ``zod`` is not yet a UI
 * dependency (the tech-stack add is deferred). The hand-rolled
 * guards here mirror the snapshot-types module's M2 pattern.
 *
 * Cross-references:
 *   - REQ-UI-006 (live update marker patch ≤ 100 ms).
 *   - REQ-UI-008 (auto reconnect).
 *   - REQ-UI-009 (snapshot before SSE resume).
 *   - CLAUDE.md §2.2 (zod at trust boundaries — deferred).
 *   - CLAUDE.md §5.10 (typed openapi.yaml is single source of truth).
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py
 *     §SseStatusPayload / §SseDetectionPayload / §SseAlertPayload.
 *   - packages/meridian_bff/src/meridian_bff/_query_sse.py
 *     §_format_sse_status / §_format_sse_detection / §_format_sse_alert.
 *   - ADR-016 §1 (snapshot + SSE pattern) + §3 (reconnect-via-snapshot).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md.
 */

/**
 * Mirror of the BFF's :class:`~meridian_bff._geometry.StatusGeometry`
 * Pydantic model — the projected WGS84 GeoJSON rings the operator map's
 * field-of-view / obscuration / coverage layers render for one platform's
 * latest ``Status`` (PR5b; REQ-UI-070 / REQ-QUERY-027; ADR-032 §4.2).
 *
 * Each "ring" is a ``number[][]`` — an array of ``[longitude, latitude]``
 * 2-tuples, already projected to WGS84 by the BFF
 * (:func:`meridian_bff._geometry.projected_ring` off ``Area.projected_outline``).
 *
 *   - ``field_of_view`` — at most ONE ring (a platform reports a single
 *     FoV), or ``null`` when the platform reported no field-of-view.
 *   - ``obscuration`` / ``coverage`` — a LIST of rings (a platform may
 *     report several regions); an empty list when none.
 *
 * # Hand-rolled, not codegen'd
 *
 * The REST ``latest-status`` path re-exports the codegen'd
 * ``components["schemas"]["StatusGeometry"]`` (see
 * :file:`entity-details/use-producer-latest-status.ts`), but the SSE route
 * carries no ``response_model`` so its payloads never surface in
 * ``openapi.yaml`` — this whole module is therefore hand-rolled to mirror
 * the Python Pydantic models verbatim (see the module docstring). The
 * shape here intentionally matches the SSE wire bytes
 * (:func:`_query_sse._format_sse_status` emits all three fields via
 * ``model_dump(mode="json")``), so ``obscuration`` / ``coverage`` are the
 * always-present list form rather than the codegen's ``?``-optional form.
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry) / REQ-QUERY-027 (status geometry).
 *   - ADR-032 §4.2 (Status geometry data model).
 *   - packages/meridian_bff/src/meridian_bff/_geometry.py §StatusGeometry.
 */
export interface StatusGeometry {
  /**
   * Field-of-view outline as a single GeoJSON ``[lon, lat]`` ring;
   * ``null`` when the platform reported no field-of-view geometry.
   */
  field_of_view: number[][] | null;
  /** Obscuration regions, each a GeoJSON ``[lon, lat]`` ring. */
  obscuration: number[][][];
  /** Coverage regions, each a GeoJSON ``[lon, lat]`` ring. */
  coverage: number[][][];
}

/**
 * Mirror of the BFF's :class:`SseStatusPayload` Pydantic model.
 *
 * Field-for-field equivalent to
 * :file:`packages/meridian_bff/src/meridian_bff/_query_api.py`
 * §SseStatusPayload. Times are ISO-8601 strings (Pydantic's default
 * datetime serialization with timezone info preserved); numeric
 * fields are floats / ints matching the proto's wire shape.
 *
 * Cross-references:
 *   - schemas/proto/meridian/v1/data/status.proto §Status.
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py
 *     §SseStatusPayload.
 */
export interface SseStatusPayload {
  /** SAPIENT Producer identifier. */
  producer_id: string;
  /** Producer-side timestamp, ISO-8601 UTC string. */
  time: string;
  /** WGS84 latitude in decimal degrees. */
  latitude_deg: number;
  /** WGS84 longitude in decimal degrees. */
  longitude_deg: number;
  /** WGS84 elevation in metres. */
  elevation_m: number;
  /** Schema version for the source row. */
  schema_version: number;
  /** SAPIENT ``HealthState`` enum integer value. */
  health_state: number;
  /** SAPIENT ``Mode`` enum integer value. */
  mode: number;
  /** Producer battery percentage [0, 100]; ``null`` when absent. */
  battery_pct: number | null;
  // NOTE: firmware_version / temperature_c / signal_strength_dbm were dropped
  // from the BFF status wire model in the ADR-032 D11 SAPIENT canonical re-base
  // (they had no BSI slot); the UI no longer carries them.
  /**
   * CDS originating-network label when this status arrived via the Cross
   * Domain Solution (issue #278); ``null``/absent when locally-ingested.
   * Drives the map CDS-provenance indicator (REQ-UI-014).
   */
  source_network?: string | null;
  /**
   * Projected field-of-view / obscuration / coverage geometry for this
   * status (PR4 4e slice; REQ-UI-070 / REQ-QUERY-027). ``null`` when the
   * platform reported no drawable geometry; **absent** on an older BFF that
   * predates the 4e slice — both consume to "no geometry" (the map's
   * geometry store drops the platform's polygons). The per-platform
   * geometry store (:file:`map/geometry-store.ts`) feeds the FoV / coverage
   * / obscuration polygon layers from this field.
   *
   * Cross-references:
   *   - ADR-032 §4.2 (Status geometry data model).
   *   - packages/meridian_bff/src/meridian_bff/_query_api.py
   *     §SseStatusPayload.geometry.
   */
  geometry?: StatusGeometry | null;
}

/**
 * Mirror of the BFF's :class:`SseDetectionPayload` Pydantic model —
 * the live-Detection feed (REQ-QUERY-028; ADR-032, where Detection
 * supersedes the retired data-plane Event).
 *
 * A subset of ``DetectionItem``: the live NATS feed lacks the
 * ingest-side metadata columns (``source_aggregator_id`` /
 * ``ingress_timestamp`` / ``message_hash``) and the serialized
 * ``payload``, so this carries only the typed list/map columns.
 *
 * **Position is nullable.** An unprojectable BSI range/bearing
 * (REQ-INGEST-044) yields ``null`` lat/lng/elev — unlike the status
 * feed, a missing position is *normal* here, not an error. The map
 * layer (PR5b) anchors the glyph at the platform marker in that case.
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py
 *     §SseDetectionPayload.
 *   - REQ-QUERY-028 (live Detection SSE feed).
 *   - REQ-INGEST-044 (nullable position).
 *   - ADR-032 (Detection supersedes Event).
 */
export interface SseDetectionPayload {
  /** SAPIENT DetectionReport identifier. */
  report_id: string;
  /** Tracked-object identifier within the report. */
  object_id: string;
  /** Source Producer. */
  producer_id: string;
  /** Detection timestamp, ISO-8601 UTC string. */
  time: string;
  /** WGS84 latitude in decimal degrees; ``null`` when unprojectable. */
  latitude_deg: number | null;
  /** WGS84 longitude in decimal degrees; ``null`` when unprojectable. */
  longitude_deg: number | null;
  /** WGS84 elevation in metres; ``null`` when unprojectable. */
  elevation_m: number | null;
  /** Object state string (e.g., ``"CONFIRMED"``); ``null`` when absent. */
  state: string | null;
  /** Associated tasking identifier; ``null`` when untasked. */
  task_id: string | null;
  /** Detection confidence [0, 1]; ``null`` when absent. */
  detection_confidence: number | null;
  /** Primary classification label; ``null`` when unclassified. */
  primary_class: string | null;
  /** Schema version for the source row. */
  schema_version: number;
  /**
   * CDS originating-network label when this detection arrived via the
   * Cross Domain Solution (issue #278); ``null``/absent when
   * locally-ingested.
   */
  source_network?: string | null;
  /**
   * The object track this detection belongs to (ADR-041; REQ-QUERY-053) —
   * UUIDv5 over ``(producer_id, object_id)``, derived by the BFF. Optional
   * so a frame from an older BFF still passes the guard; the client treats
   * an absent/empty id as "no track" and never hides such a glyph.
   */
  track_id?: string;
}

/**
 * Mirror of the BFF's :class:`SseAlertPayload` Pydantic model — the
 * live-Alert counterpart driving the Flow-F notification surface
 * (ADR-029 §5).
 *
 * A subset of ``AlertItem``: the live NATS feed lacks the ingest-side
 * metadata, the serialized ``payload``, and the dismissal columns (an
 * alert arriving live is by definition not-yet-dismissed). ``priority``
 * (with ``alert_type`` / ``status``) is the Flow-F severity driver and
 * is always present — the toast-vs-banner surface keys off it per
 * ADR-029 §5. Position is **nullable** (REQ-INGEST-044), same as the
 * detection feed.
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_api.py
 *     §SseAlertPayload.
 *   - ADR-029 §5 (priority → Flow-F toast/banner surface mapping).
 *   - REQ-QUERY-025 (Alert read surface).
 *   - REQ-INGEST-044 (nullable position).
 */
export interface SseAlertPayload {
  /** SAPIENT Alert identifier. */
  alert_id: string;
  /** Source Producer. */
  producer_id: string;
  /** Integrating Aggregator that multiplexed the Producer. */
  aggregator_id: string;
  /** Alert-received timestamp, ISO-8601 UTC string. */
  received_at: string;
  /** SAPIENT ``AlertType`` enum integer value. */
  alert_type: number;
  /** SAPIENT alert ``Status`` enum integer value. */
  status: number;
  /** SAPIENT priority enum integer value — Flow-F severity driver. */
  priority: number;
  /** Free-text alert description; ``null`` when absent. */
  description: string | null;
  /** Associated region identifier; ``null`` when absent. */
  region_id: string | null;
  /** Alert ranking score; ``null`` when absent. */
  ranking: number | null;
  /** Alert confidence [0, 1]; ``null`` when absent. */
  confidence: number | null;
  /** WGS84 latitude in decimal degrees; ``null`` when unprojectable. */
  latitude_deg: number | null;
  /** WGS84 longitude in decimal degrees; ``null`` when unprojectable. */
  longitude_deg: number | null;
  /** WGS84 elevation in metres; ``null`` when unprojectable. */
  elevation_m: number | null;
  /** Schema version for the source row. */
  schema_version: number;
  /**
   * CDS originating-network label when this alert arrived via the Cross
   * Domain Solution (issue #278); ``null``/absent when locally-ingested.
   */
  source_network?: string | null;
}

/**
 * The cross-session alert-dismissal fan-out envelope (ADR-029 [MVP]
 * item 6; the "4e fan-out").
 *
 * Unlike the other SSE payloads this is **not** a proto-derived /
 * Pydantic-modelled shape — the BFF hand-builds it in
 * :func:`_format_sse_alert_dismissed` from a raw core-NATS dismissal
 * relay and emits it to EVERY connected session so all operators drop
 * the dismissed alert from their notification surface. Hand-defined
 * here to match that plain-JSON envelope verbatim.
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_sse.py
 *     §_format_sse_alert_dismissed.
 *   - ADR-029 [MVP] item 6 (cross-session dismissal fan-out).
 */
export interface SseAlertDismissedPayload {
  /** Identifier of the alert that was dismissed. */
  alert_id: string;
  /** Dismissal timestamp, ISO-8601 UTC string. */
  dismissed_at: string;
  /** Username of the operator who dismissed the alert. */
  dismissed_by: string;
}

/**
 * SSE event envelope discriminated union. The BFF emits one of
 * ``event: status`` / ``event: detection`` / ``event: alert`` /
 * ``event: alert-dismissed`` followed by a ``data:`` line carrying the
 * JSON payload. This union is the parsed-and-tagged result downstream of
 * the SSE-line parser in :file:`use-sse.ts`.
 *
 * Note: the ``event:`` token here is the SSE *wire field name* (per the
 * HTML5 EventSource spec — each frame's event-type line), NOT the retired
 * data-plane Event kind. The data-plane Event surface was removed in the
 * ADR-032 cascade (PR6); Detection + Alert supersede it. The BFF still
 * emits an ``event: event`` frame until a later increment regenerates its
 * routes — :func:`parseSseMessage` simply ignores that frame name now
 * (forward/back-compat: unknown event names return ``null``).
 *
 * Cross-references:
 *   - packages/meridian_bff/src/meridian_bff/_query_sse.py
 *     §_format_sse_status / §_format_sse_detection / §_format_sse_alert /
 *     §_format_sse_alert_dismissed.
 *   - ADR-032 (Detection/Alert supersede Event).
 */
export type SseMessage =
  | { kind: "status"; payload: SseStatusPayload }
  | { kind: "detection"; payload: SseDetectionPayload }
  | { kind: "alert"; payload: SseAlertPayload }
  | { kind: "alert-dismissed"; payload: SseAlertDismissedPayload }
  | { kind: "meridian-dropped"; payload: SseDroppedPayload };

/**
 * The slow-consumer announcement (``event: meridian-dropped``; REQ-UI-197).
 * The BFF's per-client queue drops the oldest frame on overflow and, before
 * the next line it sends, announces the connection's cumulative drop count
 * (``_query_sse.py`` §SseClientStream.stream). The count only grows for the
 * life of one connection and restarts at zero on a reconnect.
 */
export interface SseDroppedPayload {
  /** Frames this connection has lost so far — cumulative, monotonic. */
  readonly dropped: number;
}

/** Type guard for :type:`SseDroppedPayload`. */
export function isSseDroppedPayload(value: unknown): value is SseDroppedPayload {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const dropped = (value as { dropped?: unknown }).dropped;
  return typeof dropped === "number" && Number.isInteger(dropped) && dropped >= 0;
}

/**
 * Type guard: validate that an arbitrary value matches the
 * :type:`SseStatusPayload` shape closely enough that downstream
 * consumers (the delta-merge function + marker layer) can rely on
 * the field set.
 *
 * Hand-rolled at the trust boundary in lieu of ``zod`` (see module
 * docstring). Strict on the fields the marker layer reads
 * (``producer_id``, ``latitude_deg``, ``longitude_deg``,
 * ``health_state``, ``time``); permissive on optional fields
 * (accepts ``number | null``).
 *
 * Returns ``false`` on first deviation. Callers should treat
 * ``false`` as a malformed payload and drop the message (the SSE
 * stream remains live; one bad payload doesn't poison the stream).
 *
 * Cross-references:
 *   - REQ-UI-006.
 *   - CLAUDE.md §2.2 / §8.1.
 */
export function isSseStatusPayload(value: unknown): value is SseStatusPayload {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.producer_id !== "string") return false;
  if (typeof obj.time !== "string") return false;
  if (typeof obj.latitude_deg !== "number") return false;
  if (typeof obj.longitude_deg !== "number") return false;
  if (typeof obj.elevation_m !== "number") return false;
  if (typeof obj.schema_version !== "number") return false;
  if (typeof obj.health_state !== "number") return false;
  if (typeof obj.mode !== "number") return false;
  // Nullable fields — allow null OR the expected primitive.
  // ``firmware_version`` / ``temperature_c`` / ``signal_strength_dbm`` were
  // dropped from the BFF status wire model in the ADR-032 D11 SAPIENT re-base;
  // requiring an absent field here rejected every live status delta and froze
  // the map's live updates (the UI-cascade catch-up for #377).
  if (obj.battery_pct !== null && typeof obj.battery_pct !== "number") {
    return false;
  }
  // ``geometry`` is optional + nullable (REQ-UI-070 / PR4 4e). Accept
  // absent (older BFF) or ``null`` (no drawable geometry) without
  // rejecting; reject only a present non-object. The ring contents are
  // not deep-validated here — the FoV layer's feature builder tolerates a
  // malformed ring by skipping it (operator-visibility per CLAUDE.md §8.1:
  // one bad ring never blanks the whole status stream).
  if (obj.geometry !== undefined && obj.geometry !== null && typeof obj.geometry !== "object") {
    return false;
  }
  return true;
}

/**
 * Type guard for the :type:`SseDetectionPayload` shape. Same posture
 * as :func:`isSseStatusPayload` — strict on the always-present typed
 * columns, permissive (``T | null``) on the nullable ones. Note the
 * position triple (``latitude_deg`` / ``longitude_deg`` /
 * ``elevation_m``) is nullable here (REQ-INGEST-044) — a detection
 * with an unprojectable BSI range/bearing is valid, not malformed.
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-INGEST-044.
 *   - CLAUDE.md §2.2 / §8.1.
 */
export function isSseDetectionPayload(value: unknown): value is SseDetectionPayload {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.report_id !== "string") return false;
  if (typeof obj.object_id !== "string") return false;
  if (typeof obj.producer_id !== "string") return false;
  if (typeof obj.time !== "string") return false;
  if (typeof obj.schema_version !== "number") return false;
  // Nullable position triple (REQ-INGEST-044) — allow null OR number.
  if (obj.latitude_deg !== null && typeof obj.latitude_deg !== "number") {
    return false;
  }
  if (obj.longitude_deg !== null && typeof obj.longitude_deg !== "number") {
    return false;
  }
  if (obj.elevation_m !== null && typeof obj.elevation_m !== "number") {
    return false;
  }
  // Other nullable typed columns.
  if (obj.state !== null && typeof obj.state !== "string") return false;
  if (obj.task_id !== null && typeof obj.task_id !== "string") return false;
  if (obj.detection_confidence !== null && typeof obj.detection_confidence !== "number") {
    return false;
  }
  if (obj.primary_class !== null && typeof obj.primary_class !== "string") {
    return false;
  }
  // ADR-041: optional (an older BFF omits it); when present it is a string.
  if (obj.track_id !== undefined && typeof obj.track_id !== "string") {
    return false;
  }
  return true;
}

/**
 * Type guard for the :type:`SseAlertPayload` shape. Same posture as
 * the others. ``priority`` (the Flow-F severity driver — ADR-029 §5)
 * is required; position is nullable (REQ-INGEST-044).
 *
 * Cross-references:
 *   - REQ-UI-006 / REQ-INGEST-044 / ADR-029 §5.
 *   - CLAUDE.md §2.2 / §8.1.
 */
export function isSseAlertPayload(value: unknown): value is SseAlertPayload {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.alert_id !== "string") return false;
  if (typeof obj.producer_id !== "string") return false;
  if (typeof obj.aggregator_id !== "string") return false;
  if (typeof obj.received_at !== "string") return false;
  if (typeof obj.alert_type !== "number") return false;
  if (typeof obj.status !== "number") return false;
  if (typeof obj.priority !== "number") return false;
  if (typeof obj.schema_version !== "number") return false;
  // Nullable typed columns.
  if (obj.description !== null && typeof obj.description !== "string") {
    return false;
  }
  if (obj.region_id !== null && typeof obj.region_id !== "string") return false;
  if (obj.ranking !== null && typeof obj.ranking !== "number") return false;
  if (obj.confidence !== null && typeof obj.confidence !== "number") {
    return false;
  }
  // Nullable position triple (REQ-INGEST-044).
  if (obj.latitude_deg !== null && typeof obj.latitude_deg !== "number") {
    return false;
  }
  if (obj.longitude_deg !== null && typeof obj.longitude_deg !== "number") {
    return false;
  }
  if (obj.elevation_m !== null && typeof obj.elevation_m !== "number") {
    return false;
  }
  return true;
}

/**
 * Type guard for the :type:`SseAlertDismissedPayload` envelope. All
 * three fields are required, non-null strings (the BFF hand-builds the
 * envelope — see :func:`_format_sse_alert_dismissed`).
 *
 * Cross-references:
 *   - ADR-029 [MVP] item 6.
 *   - CLAUDE.md §2.2 / §8.1.
 */
export function isSseAlertDismissedPayload(value: unknown): value is SseAlertDismissedPayload {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const obj = value as Record<string, unknown>;
  if (typeof obj.alert_id !== "string") return false;
  if (typeof obj.dismissed_at !== "string") return false;
  if (typeof obj.dismissed_by !== "string") return false;
  return true;
}

/**
 * Parse a single SSE event block into a tagged :type:`SseMessage`.
 *
 * SSE-line format per the HTML5 EventSource spec:
 *
 * ```
 * event: status
 * data: { "producer_id": "BD-CAM-01", ... }
 *
 * ```
 *
 * The native :class:`EventSource` API hands us the parsed
 * ``event.type`` + ``event.data`` separately, so this helper only
 * needs to dispatch on the event name (``status`` / ``detection`` /
 * ``alert`` / ``alert-dismissed``) + JSON-decode the data. The
 * retired data-plane ``event`` frame name is no longer a recognised
 * kind — it falls through to the unknown-event ``null`` return
 * (forward/back-compat). The exported pure form supports unit testing
 * without an :class:`EventSource` mock.
 *
 * Returns ``null`` for:
 *   - Unknown event types (forward-compat: a future BFF may emit new
 *     event kinds; the UI should ignore rather than crash).
 *   - Malformed JSON in ``data``.
 *   - Payloads that fail the corresponding type guard.
 *
 * Cross-references:
 *   - REQ-UI-006 / CLAUDE.md §8.1 (silent failures forbidden — the
 *     caller logs the rejection).
 */
export function parseSseMessage(eventName: string, rawData: string): SseMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawData);
  } catch {
    return null;
  }
  if (eventName === "status" && isSseStatusPayload(parsed)) {
    return { kind: "status", payload: parsed };
  }
  if (eventName === "detection" && isSseDetectionPayload(parsed)) {
    return { kind: "detection", payload: parsed };
  }
  if (eventName === "alert" && isSseAlertPayload(parsed)) {
    return { kind: "alert", payload: parsed };
  }
  if (eventName === "alert-dismissed" && isSseAlertDismissedPayload(parsed)) {
    return { kind: "alert-dismissed", payload: parsed };
  }
  if (eventName === "meridian-dropped" && isSseDroppedPayload(parsed)) {
    return { kind: "meridian-dropped", payload: parsed };
  }
  return null;
}
